import GuideLayout from './GuideLayout';

export default function GuideContabilitaAffittiBrevi() {
  return (
    <GuideLayout
      slug="contabilita-affitti-brevi"
      title="Contabilità affitti brevi 2026: cedolare 21%, 26% e regime forfettario"
      breadcrumbLabel="Contabilità affitti brevi"
      description="Come tenere la contabilità degli affitti brevi nel 2026: cedolare secca 21% sulla prima unità, 26% dal secondo immobile, regime forfettario per chi gestisce in forma imprenditoriale, decreto 145/2023 sulle commissioni OTA tracciate, scadenze F24, ricevute con marca da bollo."
      readingTime={12}
      faqs={faqs}
      howTo={howTo}
    >
      <h2>Cos'è la cedolare secca per gli affitti brevi</h2>
      <p>
        La <strong>cedolare secca</strong> è il regime fiscale opzionale introdotto dal D.Lgs. 23/2011 (art. 3) che permette di tassare i redditi da locazione con un'aliquota piatta, sostitutiva di IRPEF, addizionali e imposte di registro e bollo. Per gli affitti brevi (durata inferiore a 30 giorni) la cedolare è stata estesa dal D.L. 50/2017 e successivamente modificata dalla <strong>Legge di Bilancio 2024 (Legge 213/2023)</strong>.
      </p>
      <p>
        Dal <strong>1° gennaio 2024</strong>, la struttura è cambiata:
      </p>
      <ul>
        <li><strong>21% di cedolare</strong> sulla <strong>prima unità immobiliare</strong> destinata ad affitto breve.</li>
        <li><strong>26% di cedolare</strong> dal <strong>secondo al quarto immobile</strong> (quando il contribuente locazione breve più di un'unità per ciascun anno d'imposta).</li>
        <li>Dal <strong>quinto immobile in poi</strong>, l'attività si presume <strong>imprenditoriale</strong>: niente cedolare, scatta l'obbligo di apertura partita IVA e regime ordinario o forfettario.</li>
      </ul>

      <div className="callout">
        <strong>Attenzione:</strong> la soglia "5 immobili" è cumulativa per anno d'imposta, anche se gli immobili sono in capo a coniugi diversi nello stesso nucleo familiare. La giurisprudenza tributaria è restrittiva.
      </div>

      <h2>Cosa cambia con il decreto 145/2023 sulle OTA</h2>
      <p>
        Il D.L. 145/2023 (cosiddetto "decreto Anticipi", convertito nella Legge 191/2023) ha rivoluzionato la tracciabilità degli affitti brevi. In particolare:
      </p>
      <ul>
        <li>Le <strong>OTA</strong> (Airbnb, Booking, Vrbo, Expedia, ecc.) sono <strong>sostituto d'imposta</strong>: trattengono direttamente alla fonte la <strong>cedolare 21%</strong> sul corrispettivo lordo e versano allo Stato per conto del locatore.</li>
        <li>Le OTA inviano annualmente all'Agenzia delle Entrate la <strong>Certificazione Unica (CU)</strong> con i redditi del locatore. Il dato è precompilato nel 730/Redditi PF.</li>
        <li>Resta in capo all'host l'obbligo di:
          <ul>
            <li>verificare la CU e correggere eventuali errori (es. cedolare 21% trattenuta ma in realtà spettava 26%);</li>
            <li>versare l'<strong>integrazione 5%</strong> (per arrivare al 26%) tramite F24 entro le scadenze fiscali se si tratta del 2°/3°/4° immobile;</li>
            <li>compilare la sezione <strong>RB</strong> del modello Redditi PF;</li>
            <li>indicare il <strong>codice CIN</strong> nel quadro.</li>
          </ul>
        </li>
      </ul>

      <h2>Cosa entra in contabilità: gross vs net</h2>
      <p>
        Questo è il punto in cui la maggior parte degli host si confonde. Distinguiamo:
      </p>
      <table>
        <thead>
          <tr><th>Canale</th><th>Cosa inserisci</th><th>Cedolare</th><th>Commissioni</th></tr>
        </thead>
        <tbody>
          <tr><td>Airbnb / Booking / Vrbo</td><td>Bonifico ricevuto (NETTO)</td><td>Già trattenuta dalla OTA</td><td>Già trattenute dalla OTA</td></tr>
          <tr><td>Prenotazione diretta</td><td>Importo ospite (LORDO)</td><td>Tu versi 21% o 26%</td><td>Nessuna</td></tr>
          <tr><td>Booking diretto con bonifico</td><td>Importo bonifico (LORDO)</td><td>Tu versi 21% o 26%</td><td>Nessuna</td></tr>
        </tbody>
      </table>
      <p>
        In Locazione Turistica il flusso è automatizzato: quando registri una prenotazione iCal di Airbnb/Booking ti chiediamo <strong>quanto ti hanno bonificato</strong> (netto) e segniamo cedolare/commissioni come "già a sorgente". Per le manuali ti chiediamo l'importo <strong>lordo</strong> pagato dall'ospite e applichiamo automaticamente il 21% o il 26% in base al numero di immobili che gestisci.
      </p>

      <h2>Regime forfettario per affitti brevi</h2>
      <p>
        Quando si supera la soglia di 5 immobili in locazione breve, oppure quando si svolge l'attività in forma "imprenditoriale" (con servizi alberghieri: pulizie giornaliere, prima colazione, navetta, ecc.), si esce dalla cedolare e si entra in <strong>partita IVA</strong>.
      </p>
      <p>
        Il regime fiscale più conveniente per i piccoli host che aprono partita IVA è il <strong>regime forfettario</strong> (Legge 190/2014 e successive modifiche) che:
      </p>
      <ul>
        <li>Applica una <strong>tassazione sostitutiva del 15%</strong> (5% per i primi 5 anni in caso di nuova attività).</li>
        <li>Calcola il reddito imponibile applicando un <strong>coefficiente di redditività</strong> al fatturato lordo:
          <ul>
            <li><strong>40%</strong> per attività di affittacamere senza servizi (Codice ATECO 55.20.51)</li>
            <li><strong>67%</strong> per attività ricettive in forma alberghiera</li>
          </ul>
        </li>
        <li>Esonera da IVA, ritenute, ISA.</li>
        <li>Soglia massima di ricavi: <strong>85.000€</strong> annui.</li>
      </ul>

      <h3>Quale conviene di più?</h3>
      <p>
        Esempio numerico per 4 immobili in centro città:
      </p>
      <ul>
        <li>Fatturato lordo annuo: 60.000€</li>
        <li><strong>Con cedolare 26%</strong>: 60.000 × 26% = <strong>15.600€</strong> di imposta.</li>
        <li><strong>Con forfettario 15%</strong> (coeff. 40%): 60.000 × 40% = 24.000€ imponibile × 15% = <strong>3.600€</strong> di imposta. Più contributi INPS (~24% del reddito imponibile) ≈ 5.760€. Totale ≈ <strong>9.360€</strong>.</li>
      </ul>
      <p>
        In molti casi il forfettario è più conveniente, ma scattano gli obblighi di partita IVA, fatturazione elettronica (anche se semplificata), contributi INPS Gestione Separata o INPS commercianti, SCIA al SUAP, comunicazioni ISTAT mensili e Alloggiati Web.
      </p>

      <h2>Marca da bollo sulle ricevute</h2>
      <p>
        Il <strong>DPR 642/1972</strong> prevede l'imposta di bollo da <strong>2,00€</strong> su ogni ricevuta di importo superiore a <strong>77,47€</strong>. Per gli affitti brevi questa soglia è quasi sempre superata.
      </p>
      <ul>
        <li>La marca va apposta sulla copia <strong>destinata all'ospite</strong> (se richiesta).</li>
        <li>Non è dovuta se il pagamento è transitato da OTA (Airbnb, Booking) che hanno già emesso ricevuta fiscale per conto del locatore.</li>
        <li>Si paga con <strong>F24</strong> codice tributo <strong>1552</strong> oppure si acquista il contrassegno cartaceo.</li>
      </ul>

      <h2>Scadenze fiscali da ricordare</h2>
      <table>
        <thead>
          <tr><th>Scadenza</th><th>Adempimento</th><th>Codice F24</th></tr>
        </thead>
        <tbody>
          <tr><td>30 giugno</td><td>Saldo cedolare anno precedente + 1° acconto</td><td>1840</td></tr>
          <tr><td>30 novembre</td><td>2° acconto cedolare</td><td>1842</td></tr>
          <tr><td>16 marzo</td><td>CU OTA inviata all'AdE</td><td>—</td></tr>
          <tr><td>16 marzo</td><td>Versamento bollo virtuale annuale</td><td>2505</td></tr>
          <tr><td>31 ottobre</td><td>Modello 730 / Redditi PF</td><td>—</td></tr>
          <tr><td>Mensile/trimestrale</td><td>Tassa di soggiorno al comune</td><td>variabile</td></tr>
        </tbody>
      </table>

      <h2>Cosa puoi dedurre (e cosa no)</h2>
      <p>
        <strong>In cedolare secca: NULLA</strong>. La cedolare è un'imposta sostitutiva piatta sul lordo. Non puoi dedurre nessun costo: né IMU, né manutenzioni, né utenze, né commissioni Airbnb (anche se sono già trattenute dalla OTA prima di arrivare a te).
      </p>
      <p>
        <strong>In regime ordinario IRPEF o forfettario</strong>: i costi inerenti l'attività sono deducibili, ma con limiti:
      </p>
      <ul>
        <li>IMU dell'immobile: deducibile al 50% nel forfettario.</li>
        <li>Utenze, internet, manutenzioni: deducibili integralmente nell'ordinario, già forfetizzate nel forfettario.</li>
        <li>Commissioni Airbnb/Booking: deducibili al 100% nell'ordinario.</li>
        <li>Ammortamento mobili: 15% annuo, durata 7 anni.</li>
        <li>Costi pulizie e check-in: deducibili al 100%.</li>
      </ul>

      <h2>Come Locazione Turistica ti aiuta</h2>
      <ul>
        <li><strong>Contabilità OTA-centrica</strong>: importi netti per Airbnb/Booking, cedolare già a sorgente.</li>
        <li><strong>Auto-calcolo 21% / 26%</strong>: per ogni prenotazione manuale ti chiediamo se è prima o seconda casa.</li>
        <li><strong>Tassa di soggiorno</strong> calcolata in automatico (adulti × notti) pronta da versare al Comune.</li>
        <li><strong>Report PDF mensile/annuale</strong> da inviare al commercialista.</li>
        <li><strong>Calendario scadenze</strong> con promemoria su F24 e dichiarazioni.</li>
      </ul>

      <blockquote>
        La cedolare è uno strumento di semplificazione: zero contabilità, una sola aliquota. Ma "semplice" non significa "approssimativa": ogni notte non registrata è una multa che ti aspetta dietro l'angolo.
      </blockquote>
    </GuideLayout>
  );
}

const howTo = {
  name: 'Come gestire la contabilità degli affitti brevi con cedolare secca',
  steps: [
    { name: 'Determina il regime', text: 'Conta gli immobili che affitti per finalità turistiche nello stesso anno: 1 immobile → cedolare 21%; 2-4 immobili → cedolare 21% sul primo e 26% sugli altri; 5+ immobili → partita IVA obbligatoria.' },
    { name: 'Registra ogni prenotazione', text: 'Per le OTA inserisci il bonifico NETTO ricevuto. Per le prenotazioni dirette inserisci l\'importo LORDO pagato dall\'ospite.' },
    { name: 'Tracciamento commissioni e tassa di soggiorno', text: 'Le commissioni OTA sono già detratte dal bonifico (non separabili). La tassa di soggiorno va riscossa dall\'ospite e versata al Comune con cadenza mensile o trimestrale.' },
    { name: 'Versamento cedolare', text: 'Sulle prenotazioni OTA la cedolare 21% è già trattenuta a sorgente. Sulle prenotazioni dirette, e sull\'integrazione 5% per il 26%, versa tramite F24 codice 1840 entro il 30 giugno (saldo + acconto) e 30 novembre (secondo acconto).' },
    { name: 'Dichiarazione', text: 'Compila il quadro RB di Redditi PF indicando il codice CIN, i giorni di locazione e l\'imponibile. Verifica la CU inviata dalle OTA per eventuali correzioni.' },
  ],
};

const faqs = [
  { q: 'Quando si applica la cedolare 21% e quando il 26%?', a: 'Sulla prima unità immobiliare affittata per finalità turistiche si applica il 21%. Dal secondo al quarto immobile l\'aliquota è 26%. Dal quinto in poi l\'attività si presume imprenditoriale: niente cedolare, scatta l\'obbligo di apertura partita IVA.' },
  { q: 'Le commissioni Airbnb/Booking sono deducibili?', a: 'In regime di cedolare secca NO: la cedolare è imposta sostitutiva sul lordo. In regime ordinario IRPEF o forfettario sì, le commissioni sono deducibili al 100% come costi inerenti.' },
  { q: 'Devo fare la ricevuta all\'ospite Airbnb?', a: 'No, se la prenotazione è transitata sulla piattaforma e il pagamento è gestito da Airbnb. La OTA emette già un documento fiscale per conto del locatore. Sì invece per le prenotazioni dirette (sito proprio, passaparola, bonifico): in quel caso emetti ricevuta con marca da bollo 2€ per importi superiori a 77,47€.' },
  { q: 'La tassa di soggiorno è imponibile?', a: 'No, la tassa di soggiorno NON concorre alla formazione del reddito. È riscossa dall\'host per conto del Comune e va versata integralmente all\'ente locale. Va però rendicontata separatamente.' },
  { q: 'Come funziona il decreto 145/2023 con le OTA?', a: 'Dal 2024 le OTA sono sostituto d\'imposta: trattengono il 21% di cedolare alla fonte sul corrispettivo lordo e lo versano allo Stato per conto del locatore. Inviano annualmente la Certificazione Unica all\'Agenzia delle Entrate, che la usa per il 730 precompilato.' },
  { q: 'Cosa devo fare se ho 3 immobili e l\'OTA mi ha trattenuto il 21% su tutti?', a: 'Devi versare tu l\'integrazione del 5% (per arrivare al 26%) sugli immobili dal secondo in poi. Lo fai con F24 codice 1840 entro il 30 giugno dell\'anno successivo, in sede di saldo cedolare.' },
  { q: 'Conviene aprire partita IVA forfettaria per gli affitti brevi?', a: 'Spesso sì, sopra i 35-40k€ di fatturato annuo, specie con 3+ immobili. Il forfettario applica il 15% (5% per 5 anni se nuova attività) sul reddito imponibile calcolato con coefficiente 40% sui ricavi. Ma scattano partita IVA, INPS, SCIA e fatturazione elettronica. Vai dal commercialista per la valutazione caso per caso.' },
];
